// Borg conversation API and React Query definitions.
// Property names use snake_case to match the backend response format.
// useNamingConvention is disabled for this file via biome.mjs override.

import { secondsToMilliseconds } from "@suse-o11y/date-time";

import { request } from "../../generic/request";
import type { QueryClient } from "../../queries/react-query";
import {
	borgConversationListResponseSchema,
	borgConversationStatusSchema,
	borgConversationTranscriptSchema,
	borgIdStatusResponseSchema,
	borgStatusResponseSchema,
} from "../models/borg-schemas";
import type {
	BorgConversationCreateRequest,
	BorgConversationListResponse,
	BorgConversationMessageRequest,
	BorgConversationStatus,
	BorgConversationTranscript,
	BorgStatus,
} from "../models/borg-types";
import { SESSIONS_QUERY_NAMESPACE } from "../query-namespace";

import { borgApiPath } from "./borg-api";

// -- Query keys --

export const borgConversationStatusQueryKey = (id: undefined | string) =>
	[...SESSIONS_QUERY_NAMESPACE, "status", "conversation", id] as const;
export const borgConversationTranscriptQueryKey = (id: undefined | string) =>
	[...SESSIONS_QUERY_NAMESPACE, "transcript", "conversation", id] as const;
export const borgConversationsQueryKey = () => [...SESSIONS_QUERY_NAMESPACE, "list", "conversations"] as const;

// -- Shared constants --

export const TERMINAL_STATUSES = new Set<BorgStatus>(["completed", "failed", "timed_out", "cancelled", "canceling"]);
export const STATUS_POLL_INTERVAL = secondsToMilliseconds(2);
const LIST_IDLE_POLL_INTERVAL_SECONDS = 15;
export const LIST_IDLE_POLL_INTERVAL = secondsToMilliseconds(LIST_IDLE_POLL_INTERVAL_SECONDS);

// -- Query definitions --

export const queryBorgConversationStatus = (id: undefined | string) => ({
	queryKey: borgConversationStatusQueryKey(id),
	queryFn: async (): Promise<BorgConversationStatus> =>
		borgConversationStatusSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id ?? "")}/status`)),
		),
	enabled: id != null,
	refetchOnWindowFocus: false,
});

export const queryBorgConversationTranscript = (id: undefined | string) => ({
	queryKey: borgConversationTranscriptQueryKey(id),
	queryFn: async (): Promise<BorgConversationTranscript> =>
		borgConversationTranscriptSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id ?? "")}/transcript`)),
		),
	enabled: id != null,
	refetchOnWindowFocus: false,
});

const hasActiveConversations = (data: BorgConversationListResponse | undefined): boolean =>
	data?.conversations.some((conversation) => !TERMINAL_STATUSES.has(conversation.status)) ?? false;

export const queryBorgConversations = {
	queryKey: borgConversationsQueryKey(),
	queryFn: async (): Promise<BorgConversationListResponse> =>
		borgConversationListResponseSchema.parse(await request(borgApiPath("/conversations"))),
	refetchInterval: (data: BorgConversationListResponse | undefined) =>
		hasActiveConversations(data) ? STATUS_POLL_INTERVAL : LIST_IDLE_POLL_INTERVAL,
	refetchOnWindowFocus: true,
};

// -- Mutation definitions --

export const mutationBorgCreateConversation = {
	mutationFn: async (params: BorgConversationCreateRequest) =>
		borgIdStatusResponseSchema.parse(await request(borgApiPath("/conversations"), { method: "POST", body: params })),
};

export const mutationBorgSendMessage = (id: string, queryClient: QueryClient) => ({
	mutationFn: async (params: BorgConversationMessageRequest) =>
		borgIdStatusResponseSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id)}/messages`), {
				method: "POST",
				body: params,
			}),
		),
	onSuccess: () => {
		queryClient.invalidateQueries([{}, ...borgConversationStatusQueryKey(id)]);
		queryClient.invalidateQueries([{}, ...borgConversationTranscriptQueryKey(id)]);
	},
});

export const mutationBorgDeleteConversation = (queryClient: QueryClient) => ({
	mutationFn: async (id: string) =>
		borgStatusResponseSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id)}`), { method: "DELETE" }),
		),
	onSuccess: () => {
		queryClient.invalidateQueries([{}, ...borgConversationsQueryKey()]);
	},
});

export const mutationBorgRenameConversation = (queryClient: QueryClient) => ({
	mutationFn: async ({ id, displayName }: { id: string; displayName: string }) =>
		borgStatusResponseSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id)}/name`), {
				method: "PUT",
				body: { display_name: displayName },
			}),
		),
	onSuccess: () => {
		queryClient.invalidateQueries([{}, ...borgConversationsQueryKey()]);
	},
});

export const mutationBorgCancelConversation = {
	mutationFn: async (id: string) =>
		borgIdStatusResponseSchema.parse(
			await request(borgApiPath(`/conversations/${encodeURIComponent(id)}/cancel`), { method: "POST" }),
		),
};
